import { UsercentricsScript } from '@s-group/react-usercentrics';
import { AppProps } from 'next/app';
import { DefaultSeo, NextSeo } from 'next-seo';
import { PropsWithChildren } from 'react';

import ContextProvider from '@/cutils/context/ContextProvider';
import { useMaintainScrollPosition } from '@/cutils/hooks/useMaintainScrollPosition';
import { AppShellPageProps } from '@/cutils/page-context/server-side-props';
import { useApollo } from '@/utils/apollo/client';

import ErrorBoundary from '../Error/ErrorBoundary';
import { ErrorServerError } from '../Error/ErrorServerError';
import { Footer } from '../Footer/Footer';
import { HTMLHead } from '../HTMLHead/HTMLHead';
import { Header } from '../Header/Header';

import styles from './AppShellLayout.module.scss';

export function AppShellLayout({
	pageProps,
	children,
	nofollow,
	className = '',
}: PropsWithChildren<Pick<AppProps<AppShellPageProps>, 'pageProps'> & { nofollow: boolean; className?: string }>) {
	const apolloClient = useApollo(pageProps);
	const baseUrl = pageProps.environment.constants.BASE_URL ?? '';
	const appUrl = pageProps.environment.constants.appUrl;

	useMaintainScrollPosition();

	return (
		<>
			{/* add `noindex` on non-prod environments */}
			{nofollow && <DefaultSeo dangerouslySetAllPagesToNoFollow={true} dangerouslySetAllPagesToNoIndex={true} />}

			{/* Injects static meta tags into the `<head />`. */}
			<HTMLHead baseUrl={baseUrl} appUrl={appUrl} />

			{/* Injects dynamic meta tags into the `<head />`. */}
			<NextSeo {...pageProps.defaultSeoConfig} />

			<ContextProvider apolloClient={apolloClient} environment={pageProps.environment}>
				<Header navigation={pageProps.navigation} navigationTitle={pageProps.navigationTitle} />

				<main className={`${styles.content} ${className}`}>
					<ErrorBoundary fallback={<ErrorServerError />}>{children}</ErrorBoundary>
				</main>
				<Footer bayernNavigation={pageProps.navigation.bayern} />
			</ContextProvider>

			{/* This is the ID for the live configuration, if you need access data to Usercentrics please contact Johanna Borrajoconde */}
			<UsercentricsScript settingsId={pageProps.environment.keys.USERCENTRICS_ID} />
		</>
	);
}
